// Sticky filter bar for Insights.
// The Overview toolbar now shows quick period toggles + a single "Filters"
// button that opens a modal grouping every advanced filter (owner, pipeline,
// source, deal size, deal kind) with the new premium PragmaSelect dropdowns.
import { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import PragmaSelect from "./PragmaSelect";

const PERIODS = [
  { k: "week",    label: "Week" },
  { k: "month",   label: "Month" },
  { k: "quarter", label: "Quarter" },
  { k: "year",    label: "YTD" },
  { k: "12mo",    label: "12mo" },
];

const SIZE_OPTIONS = [
  { value: "",         label: "Any size" },
  { value: "0-10k",    label: "< $10k" },
  { value: "10-50k",   label: "$10k – $50k" },
  { value: "50-100k",  label: "$50k – $100k" },
  { value: "100k+",    label: "> $100k" },
];

const DEAL_KIND_OPTIONS = [
  { value: "",            label: "Acquisition + Growth" },
  { value: "acquisition", label: "Acquisition only" },
  { value: "growth",      label: "Growth only" },
];

// Which filter keys count as "active" (i.e. non-default) for the badge counter.
const ADVANCED_KEYS = ["owner_id", "pipeline_id", "source", "deal_size_band", "deal_kind"];

export default function InsightsFilterBar({ value, onChange, canFilterOwner }) {
  const [users, setUsers] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [sources, setSources] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, p, s] = await Promise.all([
          api.get("/users").catch(() => ({ data: [] })),
          api.get("/pipelines").catch(() => ({ data: [] })),
          api.get("/sources").catch(() => ({ data: [] })),
        ]);
        setUsers(u.data); setPipelines(p.data); setSources(s.data);
      } catch { /* ok */ }
    })();
  }, []);

  const activeCount = useMemo(() =>
    ADVANCED_KEYS.reduce((n, k) => n + (value[k] ? 1 : 0), 0)
  , [value]);

  const set = (k, v) => onChange({ ...value, [k]: v });

  return (
    <>
      <div
        data-testid="insights-filter-bar"
        style={{
          position: "sticky", top: 60, zIndex: 5,
          background: "color-mix(in oklch, var(--pg-bg) 90%, transparent)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--pg-border)",
          padding: "12px 32px",
          display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
        }}
      >
        <div className="flex gap-1" data-testid="filter-period">
          {PERIODS.map(p => (
            <button
              key={p.k}
              onClick={() => set("period", p.k)}
              className={`pg-btn ${value.period === p.k ? "pg-btn-active" : ""}`}
              style={{ fontSize: 12 }}
              data-testid={`filter-period-${p.k}`}
            >{p.label}</button>
          ))}
        </div>

        <div style={{ width: 1, height: 20, background: "var(--pg-border)" }} />

        <button
          onClick={() => setModalOpen(true)}
          className="pg-btn"
          data-testid="filter-open-modal"
          style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <SlidersHorizontal size={13} />
          Filters
          {activeCount > 0 && (
            <span
              data-testid="filter-active-count"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                minWidth: 18, height: 18, padding: "0 6px",
                borderRadius: 999, marginLeft: 4,
                background: "var(--pg-accent, #5B4FE8)", color: "#fff",
                fontSize: 10, fontWeight: 700, letterSpacing: 0.2,
              }}
            >{activeCount}</span>
          )}
        </button>

        {activeCount > 0 && (
          <button
            onClick={() => {
              const cleared = { ...value };
              ADVANCED_KEYS.forEach((k) => { cleared[k] = ""; });
              onChange(cleared);
            }}
            className="mono text-[11px] uppercase tracking-wider"
            style={{ color: "var(--pg-text-mute)", background: "none", border: 0, cursor: "pointer" }}
            data-testid="filter-clear-all"
          >Clear filters</button>
        )}
      </div>

      {modalOpen && (
        <FilterModal
          value={value}
          onChange={onChange}
          canFilterOwner={canFilterOwner}
          users={users}
          pipelines={pipelines}
          sources={sources}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

function FilterModal({ value, onChange, canFilterOwner, users, pipelines, sources, onClose }) {
  const [draft, setDraft] = useState(value);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const apply = () => { onChange(draft); onClose(); };
  const reset = () => {
    const cleared = { ...draft };
    ADVANCED_KEYS.forEach((k) => { cleared[k] = ""; });
    setDraft(cleared);
  };

  const ownerOptions = [
    { value: "", label: "All owners" },
    ...users.map((u) => ({ value: u.id, label: u.name, hint: u.job_title || u.email })),
  ];
  const pipelineOptions = (() => {
    const acq = pipelines.filter((p) => (p.pipeline_kind || "acquisition") === "acquisition");
    const gro = pipelines.filter((p) => p.pipeline_kind === "growth");
    if (acq.length && gro.length) {
      return [
        { value: "", label: "All pipelines" },
        { group: "Acquisition", options: acq.map((p) => ({ value: p.id, label: p.name })) },
        { group: "Growth",      options: gro.map((p) => ({ value: p.id, label: p.name, hint: p.growth_kind })) },
      ];
    }
    return [{ value: "", label: "All pipelines" }, ...pipelines.map((p) => ({ value: p.id, label: p.name }))];
  })();
  const sourceOptions = [
    { value: "", label: "All sources" },
    ...sources.map((s) => ({ value: s.name, label: s.name })),
  ];

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.35)",
          backdropFilter: "blur(2px)", zIndex: 300,
          animation: "invModalFade 0.15s ease-out",
        }}
        data-testid="filter-modal-backdrop"
      />
      <div
        role="dialog" aria-modal="true"
        data-testid="filter-modal"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "min(520px, 92vw)", zIndex: 310,
          background: "var(--pg-surface, #fff)",
          border: "1px solid var(--pg-border)",
          borderRadius: 14,
          boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
          overflow: "hidden",
          animation: "invModalRise 0.18s ease-out",
        }}
      >
        <div style={{
          padding: "18px 22px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid var(--pg-border)",
        }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: "var(--pg-text)", margin: 0 }}>Filters</h3>
            <div className="mono text-[10px] text-mute uppercase tracking-wider" style={{ marginTop: 3 }}>
              Refine your Insights view
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="filter-modal-close"
            style={{
              width: 32, height: 32, border: 0, background: "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--pg-text-mute)", cursor: "pointer", borderRadius: 6,
            }}
          ><X size={16} /></button>
        </div>

        <div style={{ padding: 22, display: "grid", gap: 16 }}>
          {canFilterOwner && (
            <PragmaSelect
              label="Owner"
              value={draft.owner_id || ""}
              onChange={(v) => set("owner_id", v)}
              options={ownerOptions}
              placeholder="All owners"
              testId="filter-owner"
              clearable
            />
          )}
          <PragmaSelect
            label="Pipeline"
            value={draft.pipeline_id || ""}
            onChange={(v) => set("pipeline_id", v)}
            options={pipelineOptions}
            placeholder="All pipelines"
            testId="filter-pipeline"
            clearable
          />
          <PragmaSelect
            label="Deal kind"
            value={draft.deal_kind || ""}
            onChange={(v) => set("deal_kind", v)}
            options={DEAL_KIND_OPTIONS}
            testId="filter-deal-kind"
          />
          <PragmaSelect
            label="Lead source"
            value={draft.source || ""}
            onChange={(v) => set("source", v)}
            options={sourceOptions}
            placeholder="All sources"
            testId="filter-source"
            clearable
          />
          <PragmaSelect
            label="Deal size"
            value={draft.deal_size_band || ""}
            onChange={(v) => set("deal_size_band", v)}
            options={SIZE_OPTIONS}
            testId="filter-size"
          />
        </div>

        <div style={{
          padding: "14px 22px",
          borderTop: "1px solid var(--pg-border)",
          background: "var(--pg-surface-2, rgba(0,0,0,0.02))",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
        }}>
          <button
            onClick={reset}
            className="mono text-[11px] uppercase tracking-wider"
            style={{ color: "var(--pg-text-mute)", background: "none", border: 0, cursor: "pointer" }}
            data-testid="filter-modal-reset"
          >Reset all</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} className="pg-btn" data-testid="filter-modal-cancel">Cancel</button>
            <button onClick={apply} className="pg-btn pg-btn-primary" data-testid="filter-modal-apply">Apply filters</button>
          </div>
        </div>
      </div>
    </>
  );
}

// Convert a size band into (min, max) numbers to send to the API.
export function sizeBandToRange(band) {
  switch (band) {
    case "0-10k":   return { deal_size_min: 0, deal_size_max: 10000 };
    case "10-50k":  return { deal_size_min: 10000, deal_size_max: 50000 };
    case "50-100k": return { deal_size_min: 50000, deal_size_max: 100000 };
    case "100k+":   return { deal_size_min: 100000 };
    default:        return {};
  }
}
